import { cn } from "@portal/components/lib/utils";
import { Text } from "@portal/components/primitives";
import type { FC, ReactNode } from "react";

type ReportSectionProps = {
	title: string;
	summary?: ReactNode;
	testId: string;
	children: ReactNode;
};

export const ReportSection: FC<ReportSectionProps> = ({ title, summary, testId, children }) => (
	<section className="flex flex-col gap-3 border-b border-bc-default pb-5" data-testid={testId}>
		<div className="flex flex-wrap items-baseline justify-between gap-2">
			<Text type="title" color="primary" role="heading" aria-level={3}>
				{title}
			</Text>
			{summary}
		</div>
		{children}
	</section>
);

type StatusSummaryProps = {
	tone: "danger" | "confirm" | "warning";
	children: ReactNode;
	testId?: string;
};

export const StatusSummary: FC<StatusSummaryProps> = ({ tone, children, testId }) => (
	<div className="flex items-center gap-2" data-testid={testId}>
		<span
			aria-hidden
			className={cn(
				"h-2 w-2 rounded-full",
				tone === "danger" && "bg-danger",
				tone === "confirm" && "bg-confirm",
				tone === "warning" && "bg-warning",
			)}
		/>
		<Text type="body" color={tone}>
			{children}
		</Text>
	</div>
);

export const SectionSummary: FC<{ children: ReactNode }> = ({ children }) => (
	<Text type="body" color="secondary">
		{children}
	</Text>
);

type FactProps = {
	label: string;
	children: ReactNode;
	testId?: string;
};

/** Uppercase label over a value, the hero's and detail rows' fact layout. */
export const Fact: FC<FactProps> = ({ label, children, testId }) => (
	<div className="flex min-w-0 flex-col gap-2.5" data-testid={testId}>
		<Text type="eyebrow" color="tertiary">
			{label}
		</Text>
		{children}
	</div>
);
